/**
 * The practice page (docs/ux/registry.md "The practice page"): Beam's
 * details as the statutory forms' assessor block prints them (D-0055), then
 * the letterhead and footer strip as the composer prints them across the
 * first page of every generated report, each with its preview at page
 * proportion, its version and who added it, and an upload that adds a
 * version; the change history beneath. The phone reads; uploads and edits
 * are a desktop task.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';
import { toast } from 'sonner';
import { say } from '@/app/announcer';
import { useOnline } from '@/app/online';
import type { PracticeImage, PracticeImageKind, PracticePatch, PracticeRecord } from '@/app/types';
import { useIsPhone } from '@/app/viewport';
import { PageHeader } from '@/components/shell/PageHeader';
import { useShellWidth } from '@/components/shell/context';
import { ErrorPanel } from '@/components/shell/states';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { dateTime, timeOfDay } from '@/lib/format';
import { ConfigPanel, Field, ReadGrid, ReadPair, type PanelSaveState } from './ConfigPanel';
import { ChangeHistory } from './panels';
import { RegistryPill } from './RegistryPill';
import { UploadField } from './UploadField';
import { practiceImageUrl, practiceKey, refusalOf, uploadPracticeImage, usePractice, useUpdatePractice } from './useRegistry';

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = { mime: ['image/png'], extensions: ['.png'], words: 'a PNG' };
const IDLE: PanelSaveState = { saving: false, error: null, conflict: false, savedAt: null };

const BANDS: Record<PracticeImageKind, { title: string; where: string; without: string }> = {
  letterhead: { title: 'Letterhead', where: 'Across the top of the first page.', without: 'No letterhead on file. The practice name prints in the header instead.' },
  footer: { title: 'Footer strip', where: 'Across the bottom of the first page.', without: 'No footer strip on file. The first page ends as later pages do, with the report name and the page numbers.' },
};

export function PracticePage() {
  useShellWidth('narrow');
  const phone = useIsPhone();
  const online = useOnline();
  const client = useQueryClient();
  const query = usePractice();
  const header = <PageHeader title="Practice" description="Beam's details as the statutory forms print them, and the letterhead and footer strip printed across the first page of every generated report." tabs={<RegistryPill current="practice" />} />;

  if (query.isError) return <div className="flex flex-col gap-3">{header}<ErrorPanel message="The practice record could not be loaded." onRetry={() => void query.refetch()} /></div>;
  if (query.isPending) return <div className="flex flex-col gap-3">{header}<Skeleton className="h-64 rounded-xl" aria-label="Loading the practice" /></div>;
  const practice = query.data;

  function landed(kind: PracticeImageKind, record: PracticeRecord) {
    client.setQueryData(practiceKey, record);
    toast.success(`${BANDS[kind].title} version ${record[kind].version} added.`);
    say(`${BANDS[kind].title} version ${record[kind].version} added; it prints on the next generated report.`);
  }

  return (
    <div className="flex flex-col gap-3">
      {header}
      <DetailsPanel practice={practice} phone={phone} offline={!online} onReload={() => void query.refetch()} />
      {(['letterhead', 'footer'] as const).map((kind) => <BandPanel key={kind} kind={kind} image={practice[kind]} phone={phone} offline={!online} onLanded={(r) => landed(kind, r)} />)}
      <ChangeHistory audit={practice.audit} phone={phone} />
    </div>
  );
}

/* The practice's name and contact details: one patch with the version read; the forms' assessor block prints them. */
function DetailsPanel({ practice, phone, offline, onReload }: { practice: PracticeRecord; phone: boolean; offline: boolean; onReload: () => void }) {
  const id = useId();
  const update = useUpdatePractice();
  const read = { name: practice.name, address: practice.contact?.address ?? '', phone: practice.contact?.phone ?? '', email: practice.contact?.email ?? '' };
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(read);
  const [state, setState] = useState<PanelSaveState>(IDLE);
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => { if (!editing) { setDraft(read); setErrors({}); } }, [editing, practice.version]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = JSON.stringify(draft) !== JSON.stringify(read);
  const orNull = (s: string): string | null => (s.trim() ? s.trim() : null);

  async function save() {
    const errs: Record<string, string> = {};
    if (!draft.name.trim()) errs.name = 'Enter the practice name as it prints.';
    if (draft.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim())) errs.email = 'Enter an email address, or leave it empty.';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    const patch: PracticePatch = {};
    if (draft.name !== read.name) patch.name = draft.name.trim();
    if (draft.address !== read.address) patch.address = orNull(draft.address);
    if (draft.phone !== read.phone) patch.phone = orNull(draft.phone);
    if (draft.email !== read.email) patch.email = orNull(draft.email);
    setState({ ...IDLE, saving: true });
    try {
      await update.mutateAsync({ patch, version: practice.version });
      setState({ ...IDLE, savedAt: timeOfDay(new Date().toISOString()) });
      setEditing(false);
      toast.success('Practice details saved.');
    } catch (err) {
      const refusal = refusalOf(err);
      if (refusal.kind === 'conflict') { setState({ ...IDLE, conflict: true, error: 'Someone else changed the practice details; reload and try again.' }); return; }
      setState({ ...IDLE, error: refusal.reason ? `${refusal.reason.charAt(0).toUpperCase()}${refusal.reason.slice(1)}.` : 'The save did not complete. Try again.' });
    }
  }

  return (
    <ConfigPanel
      id="panel-practice" eyebrow="Details" description="Printed in the assessor block of the statutory forms; the composer's header uses the name when no letterhead is on file."
      editing={editing} canEdit editable={!phone} offline={offline} dirty={dirty} state={state}
      onEdit={() => { setState(IDLE); setEditing(true); }} onCancel={() => setEditing(false)} onSave={save} onReload={onReload}
      read={(
        <ReadGrid columns={phone ? 1 : 2}>
          <ReadPair label="Name" value={practice.name} />
          <ReadPair label="Phone" value={practice.contact?.phone ?? null} />
          <ReadPair label="Address" value={practice.contact?.address ?? null} span />
          <ReadPair label="Email" value={practice.contact?.email ?? null} />
        </ReadGrid>
      )}
      form={(
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Name" htmlFor={`${id}-name`} error={errors.name}>
            <Input id={`${id}-name`} data-autofocus value={draft.name} autoComplete="off" maxLength={120} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="h-9" aria-invalid={errors.name ? true : undefined} />
          </Field>
          <Field label="Phone" htmlFor={`${id}-phone`} optional>
            <Input id={`${id}-phone`} value={draft.phone} autoComplete="off" maxLength={40} inputMode="tel" onChange={(e) => setDraft({ ...draft, phone: e.target.value })} className="h-9" />
          </Field>
          <Field label="Address" htmlFor={`${id}-address`} optional className="md:col-span-2">
            <Input id={`${id}-address`} value={draft.address} autoComplete="off" maxLength={200} onChange={(e) => setDraft({ ...draft, address: e.target.value })} className="h-9" />
          </Field>
          <Field label="Email" htmlFor={`${id}-email`} optional error={errors.email}>
            <Input id={`${id}-email`} value={draft.email} autoComplete="off" maxLength={120} inputMode="email" onChange={(e) => setDraft({ ...draft, email: e.target.value })} className="h-9" aria-invalid={errors.email ? true : undefined} />
          </Field>
        </div>
      )}
    />
  );
}

function BandPanel({ kind, image, phone, offline, onLanded }: { kind: PracticeImageKind; image: PracticeImage; phone: boolean; offline: boolean; onLanded: (record: PracticeRecord) => void }) {
  const words = BANDS[kind];
  const headingId = `practice-${kind}-heading`;
  const onFile = image.assetKey !== null;
  return (
    <section aria-labelledby={headingId} data-slot="practice-panel" data-kind={kind} data-on-file={onFile ? 'true' : 'false'} className="flex flex-col gap-3 rounded-xl border border-beam-line bg-beam-paper p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id={headingId} className="eyebrow text-beam-text-muted">{words.title}</h2>
        <span className="text-[13px] text-beam-text-muted">{words.where}</span>
      </div>
      {onFile ? (
        <figure data-slot="practice-preview" className="flex flex-col gap-2">
          {/* The band at page proportion: the frame is the page's width, the image spans it edge to edge as it prints. */}
          <div className={kind === 'letterhead' ? 'rounded-t-md border border-b-0 border-beam-line bg-white pb-6 shadow-sm' : 'rounded-b-md border border-t-0 border-beam-line bg-white pt-6 shadow-sm'}>
            <img src={practiceImageUrl(kind, image.version)} alt={`${words.title} as it prints`} className="block w-full" />
          </div>
          <figcaption className="text-[13px] text-beam-text-muted">
            <span className="tabular">Version {image.version}</span>, <span className="tabular">{image.width} by {image.height}</span> pixels{image.addedAt ? `. Added ${dateTime(image.addedAt)}${image.addedBy ? ` by ${image.addedBy}` : ''}.` : '.'}
          </figcaption>
        </figure>
      ) : (
        <p data-slot="practice-empty" className="text-sm text-beam-text-muted">{words.without}</p>
      )}
      {phone ? (
        <p className="text-[13px] text-beam-text-muted">Upload from a desktop.</p>
      ) : (
        <UploadField<PracticeRecord>
          send={(file, onProgress, signal) => uploadPracticeImage(kind, file, onProgress, signal)}
          label={words.title}
          accept={ACCEPT}
          maxBytes={MAX_BYTES}
          offline={offline}
          current={onFile ? { name: 'On file' } : null}
          onLanded={onLanded}
          compact
          help="A PNG up to 5 MB, at least twice as wide as it is tall, because it prints across the page. A new version is added; earlier versions are kept."
        />
      )}
    </section>
  );
}
